import {
  Button,
  Collapse,
  Icon,
  Spinner,
  type IconName,
} from "@blueprintjs/core";
import { useGetEvents } from "@package/ui-apollo-components";
import {
  EventPayloadType,
  type GetEventsQueryVariables,
} from "@package/ui-gql/index-analytics";
import { useGetLocationListNameIdQuery } from "@package/ui-gql/index-data-access";
import { useMemo } from "react";
import { useGetEventStats } from "../../hooks/use-get-event-stats";
import { setAnalyticsOverviewBarOpen } from "../../store/slices/slice-analytics-overview-bar";
import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  formatDurationShort,
  formatPercent,
  getLastSevenDaysRange,
} from "../../utils/analytics-format-utils";
import { DashboardCard } from "../DashboardCard/DashboardCard";

// A location counts as "online" using the same 5-minute DEVICE_ACTIVE
// freshness heuristic the Locations grid's per-row status column uses.
const ONLINE_THRESHOLD_MINUTES = 5;
// A device can emit several DEVICE_ACTIVE heartbeats within the window above;
// the online-events page needs headroom over the location count to avoid
// truncating before every location's heartbeats are counted.
const ONLINE_EVENTS_PER_LOCATION_HEADROOM = 20;

function OverviewCard({
  icon,
  label,
  value,
  detail,
  loading,
}: {
  icon: IconName;
  label: string;
  value: string;
  detail?: string;
  loading?: boolean;
}) {
  return (
    <DashboardCard className="flex-1 flex flex-row items-start gap-3 min-w-40">
      <div className="rounded-lg bg-brand-green-tint p-2 text-brand-green-dark">
        <Icon icon={icon} size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold tracking-wide text-brand-muted uppercase">
          {label}
        </p>
        {loading ? (
          <Spinner size={16} className="mt-1.5" />
        ) : (
          <p className="text-2xl font-semibold text-brand-ink mt-0.5 truncate">
            {value}
          </p>
        )}
        {detail && !loading && (
          <p className="text-xs text-brand-muted mt-0.5 truncate">{detail}</p>
        )}
      </div>
    </DashboardCard>
  );
}

// Always visible above the panel content (see App.tsx) so org-wide analytics
// stay in view no matter which panel — Location, Canvas, Tag, etc. — is open.
export function AnalyticsOverviewBar() {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.analyticsOverviewBar.isOpen);

  const range = useMemo(() => getLastSevenDaysRange(), []);

  const { data: dataLocations, loading: loadingLocations } =
    useGetLocationListNameIdQuery({ variables: { page: 0, perPage: 1000 } });

  const locationIds = useMemo(
    () =>
      (dataLocations?.items
        ?.map((location) => location.id)
        .filter((id) => !!id) as string[]) ?? [],
    [dataLocations?.items],
  );

  const variablesOnline = useMemo<GetEventsQueryVariables>(() => {
    const start = new Date();
    start.setMinutes(start.getMinutes() - ONLINE_THRESHOLD_MINUTES);

    return {
      filter: {
        eventType: [EventPayloadType.DeviceActive],
        location: locationIds,
        range: { start },
      },
      page: 0,
      perPage: Math.max(
        1000,
        locationIds.length * ONLINE_EVENTS_PER_LOCATION_HEADROOM,
      ),
    };
  }, [locationIds]);

  const { data: dataOnline, loading: loadingOnline } = useGetEvents(
    variablesOnline,
    locationIds.length === 0,
  );

  const onlineCount = useMemo(() => {
    const online = new Set(
      dataOnline?.items?.map((event) => event.location).filter(Boolean),
    );
    return online.size;
  }, [dataOnline?.items]);

  const variablesStats = useMemo(() => ({ filter: { range } }), [range]);
  const { data: dataStats, loading: loadingStats } =
    useGetEventStats(variablesStats);

  const stats = dataStats?.item;
  const loading = loadingLocations || loadingOnline || loadingStats;

  const compactSummary = [
    `${onlineCount} of ${locationIds.length} online`,
    `${stats?.proofOfPlayCount ?? 0} plays`,
    `${stats?.contentOpenedCount ?? 0} opens`,
    `${formatDurationShort(stats?.avgInteractionSeconds)} avg`,
    `${stats?.qrScansCount ?? 0} scans`,
  ].join(" · ");

  return (
    <>
      <div className="flex items-center gap-2 px-4 py-2">
        <Button
          minimal
          small
          icon="pie-chart"
          rightIcon={isOpen ? "caret-down" : "caret-right"}
          text="Analytics"
          onClick={() => dispatch(setAnalyticsOverviewBarOpen(!isOpen))}
        />
        {!isOpen &&
          (loading ? (
            <Spinner size={12} />
          ) : (
            <span className="text-xs text-brand-muted truncate">
              {compactSummary}
            </span>
          ))}
      </div>
      <Collapse isOpen={isOpen}>
        <div className="flex flex-wrap gap-3 px-4 pb-3">
          <OverviewCard
            icon="desktop"
            label="Player status"
            value={`${onlineCount} of ${locationIds.length}`}
            detail="locations online"
            loading={loadingLocations || loadingOnline}
          />
          <OverviewCard
            icon="play"
            label="Proof of play"
            value={`${stats?.proofOfPlayCount ?? 0}`}
            detail="last 7 days"
            loading={loadingStats}
          />
          <OverviewCard
            icon="eye-open"
            label="Content opened"
            value={`${stats?.contentOpenedCount ?? 0}`}
            detail={`${formatPercent(stats?.contentOpenedCount ?? 0, stats?.proofOfPlayCount ?? 0)} open rate`}
            loading={loadingStats}
          />
          <OverviewCard
            icon="time"
            label="Avg. interaction"
            value={formatDurationShort(stats?.avgInteractionSeconds)}
            detail="per guest session"
            loading={loadingStats}
          />
          <OverviewCard
            icon="barcode"
            label="QR scans"
            value={`${stats?.qrScansCount ?? 0}`}
            detail={`${formatPercent(stats?.qrScansCount ?? 0, stats?.contentOpenedCount ?? 0)} of opens`}
            loading={loadingStats}
          />
        </div>
      </Collapse>
    </>
  );
}
